// src/components/FoodTracker.jsx
import React, { useEffect, useState, useCallback } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { useApp } from "../App";
import {
  getFoodEntriesByDate, addFoodEntry, deleteFoodEntry,
  getSavedMeals, addSavedMeal, deleteSavedMeal,
} from "../services/firebase";
import { calculateDailyTotals } from "../utils/dailyTotals";

const today = () => new Date().toISOString().slice(0, 10);

const MEAL_TYPES = ["breakfast", "lunch", "dinner", "snack"];
const MACRO_COLORS = { Protein: "#ff7c0a", Carbs: "#3b82f6", Fat: "#a855f7" };

const emptyForm = () => ({
  meal_name: "", meal_type: "breakfast",
  calories: "", protein: "", carbs: "", fat: "",
});

export default function FoodTracker() {
  const { user, profile } = useApp();
  const [dateStr,     setDateStr]     = useState(today());
  const [entries,     setEntries]     = useState([]);
  const [savedMeals,  setSavedMeals]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState("");
  const [form,        setForm]        = useState(emptyForm());
  const [savingMeal,  setSavingMeal]  = useState(false);
  const [activeTab,   setActiveTab]   = useState("log"); // "log" | "saved"

  const loadEntries = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const [{ data: foods }, { data: saved }] = await Promise.all([
      getFoodEntriesByDate(user.uid, dateStr),
      getSavedMeals(user.uid),
    ]);
    setEntries(foods || []);
    setSavedMeals(saved || []);
    setLoading(false);
  }, [user, dateStr]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateForm = () => {
    if (!form.meal_name.trim())         return "Enter a meal name.";
    if (!form.calories || form.calories < 0) return "Enter calories.";
    if (!form.protein  || form.protein  < 0) return "Enter protein (g).";
    if (!form.carbs    || form.carbs    < 0) return "Enter carbs (g).";
    if (!form.fat      || form.fat      < 0) return "Enter fat (g).";
    return null;
  };

  const handleAdd = async () => {
    const err = validateForm();
    if (err) { setError(err); return; }
    setError(""); setSaving(true);

    await addFoodEntry(user.uid, {
      date:      dateStr,
      meal_name: form.meal_name.trim(),
      meal_type: form.meal_type,
      calories:  parseFloat(form.calories),
      protein:   parseFloat(form.protein),
      carbs:     parseFloat(form.carbs),
      fat:       parseFloat(form.fat),
    });

    await loadEntries();
    setForm(emptyForm());
    setSaving(false);
  };

  const handleDelete = async (id) => {
    await deleteFoodEntry(user.uid, id);
    await loadEntries();
  };

  const handleSaveAsMeal = async () => {
    const err = validateForm();
    if (err) { setError(err); return; }
    setSavingMeal(true);
    await addSavedMeal(user.uid, {
      meal_name: form.meal_name.trim(),
      calories:  parseFloat(form.calories),
      protein:   parseFloat(form.protein),
      carbs:     parseFloat(form.carbs),
      fat:       parseFloat(form.fat),
    });
    await loadEntries();
    setSavingMeal(false);
  };

  const handleLogSaved = async (meal) => {
    await addFoodEntry(user.uid, {
      date:      dateStr,
      meal_name: meal.meal_name,
      meal_type: "snack",
      calories:  meal.calories,
      protein:   meal.protein,
      carbs:     meal.carbs,
      fat:       meal.fat,
    });
    await loadEntries();
  };

  const handleDeleteSaved = async (id) => {
    await deleteSavedMeal(user.uid, id);
    await loadEntries();
  };

  // Totals
  const totals    = calculateDailyTotals(entries);
  const calPct    = Math.min(100, Math.round((totals.calories / (profile?.calorie_target || 1)) * 100));
  const proteinPct = Math.min(100, Math.round((totals.protein / (profile?.protein_target || 1)) * 100));

  const pieData = [
    { name: "Protein", value: totals.protein * 4   },
    { name: "Carbs",   value: totals.carbs   * 4   },
    { name: "Fat",     value: totals.fat     * 9   },
  ].filter((d) => d.value > 0);

  const grouped = MEAL_TYPES.reduce((acc, t) => {
    acc[t] = entries.filter((e) => e.meal_type === t);
    return acc;
  }, {});

  return (
    <div className="space-y-5 animate-slide-up">
      <div className="flex items-center justify-between">
        <h1 className="section-title">Food Tracker</h1>
        <input type="date" className="input w-auto text-xs" value={dateStr}
          onChange={(e) => setDateStr(e.target.value)} max={today()} />
      </div>

      {/* ── Daily totals banner ─────────────────────────────────────── */}
      {profile && (
        <div className="card border-[#ff7c0a]/20">
          {/* Protein — prominent */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#ff7c0a]" />
                <span className="text-xs font-semibold text-[#ff7c0a] uppercase tracking-wider">Protein</span>
              </div>
              <span className="text-xs text-slate-500">{proteinPct}%</span>
            </div>
            <div className="flex items-baseline gap-2 mb-1.5">
              <span className="text-3xl font-bold text-[#ff7c0a]"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                {totals.protein}g
              </span>
              <span className="text-slate-500 text-sm">/ {profile.protein_target}g</span>
              {proteinPct >= 100 && <span className="text-emerald-400 text-xs font-semibold">✓ Hit!</span>}
            </div>
            <div className="progress-track h-2.5">
              <div className="progress-fill h-2.5 bg-[#ff7c0a]" style={{ width: `${proteinPct}%` }} />
            </div>
          </div>

          {/* Calories */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-slate-400 font-medium">Calories</span>
              <span className="text-xs text-slate-500">
                {totals.calories} / {profile.calorie_target} kcal
              </span>
            </div>
            <div className="progress-track h-1.5">
              <div
                className={`progress-fill h-1.5 ${calPct > 105 ? "bg-red-500" : "bg-slate-400"}`}
                style={{ width: `${Math.min(calPct, 100)}%` }}
              />
            </div>
          </div>

          {/* Macros row */}
          <div className="grid grid-cols-3 gap-3 pt-3 border-t border-[#1f2a3c]">
            {[
              { l: "Protein", v: totals.protein, u: "g", c: "text-[#ff7c0a]" },
              { l: "Carbs",   v: totals.carbs,   u: "g", c: "text-blue-400" },
              { l: "Fat",     v: totals.fat,      u: "g", c: "text-purple-400" },
            ].map(({ l, v, u, c }) => (
              <div key={l} className="text-center">
                <div className={`text-lg font-bold ${c}`}
                     style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{v}{u}</div>
                <div className="text-xs text-slate-600">{l}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Tabs ─────────────────────────────────────────────────────── */}
      <div className="flex bg-[#111820] rounded-lg p-1">
        {[["log", "Log Food"], ["saved", `Saved Meals (${savedMeals.length})`]].map(([v, l]) => (
          <button key={v} onClick={() => setActiveTab(v)}
            className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all duration-200 ${
              activeTab === v ? "bg-[#ff7c0a] text-white" : "text-slate-400 hover:text-slate-200"
            }`}>
            {l}
          </button>
        ))}
      </div>

      {/* ── Log food form ────────────────────────────────────────────── */}
      {activeTab === "log" && (
        <div className="card">
          <div className="text-sm font-semibold text-slate-200 mb-4">Add Entry</div>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 sm:col-span-1">
                <label className="label">Meal Name</label>
                <input className="input" placeholder="e.g. Chicken & Rice"
                  value={form.meal_name} onChange={(e) => setField("meal_name", e.target.value)} />
              </div>
              <div>
                <label className="label">Meal Type</label>
                <select className="select" value={form.meal_type}
                  onChange={(e) => setField("meal_type", e.target.value)}>
                  {MEAL_TYPES.map((t) => (
                    <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                { key: "calories", label: "Calories", ph: "450" },
                { key: "protein",  label: "Protein (g)", ph: "40" },
                { key: "carbs",    label: "Carbs (g)",   ph: "35" },
                { key: "fat",      label: "Fat (g)",     ph: "12" },
              ].map(({ key, label, ph }) => (
                <div key={key}>
                  <label className="label">{label}</label>
                  <input type="number" className="input" placeholder={ph} min="0"
                    value={form[key]} onChange={(e) => setField(key, e.target.value)} />
                </div>
              ))}
            </div>

            {error && <p className="text-red-400 text-xs">{error}</p>}

            <div className="flex gap-2 pt-1">
              <button onClick={handleAdd} disabled={saving} className="btn-primary flex-1">
                {saving ? "Adding…" : "Add Entry"}
              </button>
              <button onClick={handleSaveAsMeal} disabled={savingMeal}
                className="btn-ghost text-xs px-3">
                {savingMeal ? "Saving…" : "💾 Save Meal"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Saved meals tab ──────────────────────────────────────────── */}
      {activeTab === "saved" && (
        <div className="card">
          {savedMeals.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <span className="text-2xl block mb-2">💾</span>
              <p className="text-sm">No saved meals yet.</p>
              <p className="text-xs mt-1">Log a meal and click "Save Meal" to add it here.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {savedMeals.map((meal) => (
                <div key={meal.id}
                  className="flex items-center justify-between bg-[#111820] rounded-lg px-4 py-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-200 truncate">{meal.meal_name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {meal.calories} cal · {meal.protein}g P · {meal.carbs}g C · {meal.fat}g F
                    </div>
                  </div>
                  <div className="flex gap-2 ml-3 flex-shrink-0">
                    <button onClick={() => handleLogSaved(meal)}
                      className="btn-primary text-xs py-1.5 px-3">
                      + Log
                    </button>
                    <button onClick={() => handleDeleteSaved(meal.id)}
                      className="text-slate-600 hover:text-red-400 transition-colors text-xs px-2">
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Macro pie chart ──────────────────────────────────────────── */}
      {pieData.length > 0 && (
        <div className="card">
          <div className="text-sm font-semibold text-slate-200 mb-3">Macro Breakdown</div>
          <div className="flex items-center gap-6">
            <ResponsiveContainer width={120} height={120}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={35} outerRadius={55}
                  paddingAngle={3} dataKey="value">
                  {pieData.map((entry) => (
                    <Cell key={entry.name} fill={MACRO_COLORS[entry.name]} />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }) =>
                    active && payload?.[0] ? (
                      <div className="bg-[#1a2332] border border-[#263347] rounded-lg px-2 py-1 text-xs">
                        <span className="text-slate-300">{payload[0].name}: </span>
                        <span className="font-bold text-slate-100">
                          {Math.round(payload[0].value / 4 / (payload[0].name === "Fat" ? 9/4 : 1))}g
                        </span>
                      </div>
                    ) : null
                  }
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-2">
              {[
                { label: "Protein", kcal: totals.protein * 4, g: totals.protein, color: "bg-[#ff7c0a]" },
                { label: "Carbs",   kcal: totals.carbs   * 4, g: totals.carbs,   color: "bg-blue-500"  },
                { label: "Fat",     kcal: totals.fat     * 9, g: totals.fat,      color: "bg-purple-500"},
              ].map(({ label, g, color }) => (
                <div key={label} className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${color} flex-shrink-0`} />
                  <span className="text-xs text-slate-400 w-14">{label}</span>
                  <span className="text-xs font-semibold text-slate-200">{g}g</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Entries by meal type ─────────────────────────────────────── */}
      <div className="space-y-3">
        {loading ? (
          [1,2,3].map((i) => <div key={i} className="h-16 bg-[#161d28] rounded-xl animate-pulse" />)
        ) : entries.length === 0 ? (
          <div className="card text-center py-10 text-slate-500">
            <span className="text-3xl block mb-2">🍽️</span>
            <p className="text-sm">No food logged for {dateStr}.</p>
            <p className="text-xs mt-1 text-slate-600">Add your first meal above.</p>
          </div>
        ) : (
          MEAL_TYPES.map((type) =>
            grouped[type].length > 0 ? (
              <div key={type} className="card">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    {type}
                  </div>
                  <div className="text-xs text-slate-500">
                    {grouped[type].reduce((s, e) => s + e.calories, 0)} cal
                  </div>
                </div>
                <div className="space-y-1.5">
                  {grouped[type].map((entry) => (
                    <div key={entry.id}
                      className="flex items-center justify-between bg-[#111820] rounded-lg px-3 py-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-200 truncate">{entry.meal_name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {entry.calories} cal ·{" "}
                          <span className="text-[#ff7c0a]">{entry.protein}g P</span> ·{" "}
                          {entry.carbs}g C · {entry.fat}g F
                        </div>
                      </div>
                      <button onClick={() => handleDelete(entry.id)}
                        className="text-slate-600 hover:text-red-400 transition-colors text-xs ml-3 px-2 py-1">
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null
          )
        )}
      </div>
    </div>
  );
}
